/**
 * USFS live index pages (e.g. ijs.usfigureskating.org) often list IJS segments first,
 * then a separate "Majority 6.0 results" block with 6.0-style segment links.
 */

/** @typedef {'ijs' | '60' | 'both'} SegmentSystem */

/**
 * Find the element that introduces the 6.0 results section (heading or table caption).
 * @param {Document} doc
 * @returns {Element | null}
 */
export function findMajoritySixZeroSectionStart(doc) {
  const candidates = doc.querySelectorAll('h1, h2, h3, h4, th, td, p, caption, div, span');
  for (const el of candidates) {
    const t = (el.textContent || '').replace(/\s+/g, ' ').trim();
    if (/majority\s+6\.0\s+results/i.test(t) && t.length < 160) {
      return el;
    }
  }
  return null;
}

/**
 * Collect segment .htm/.html links, optionally restricted to IJS-only or 6.0-only relative to the Majority 6.0 heading.
 * @param {Document} doc
 * @param {string} liveScoreBaseUrl - Base URL for resolving relative hrefs
 * @param {SegmentSystem} segmentSystem - 'ijs' = blue section only, '60' = Majority 6.0 block only, 'both' = all
 * @returns {Array<{ url: string, label: string }>}
 */
export function collectSegmentLinksFromIndexDom(doc, liveScoreBaseUrl, segmentSystem = 'both') {
  const heading = findMajoritySixZeroSectionStart(doc);
  const seen = new Set();
  /** @type {Array<{ url: string, label: string }>} */
  const out = [];

  doc.querySelectorAll('a[href]').forEach((a) => {
    const href = a.getAttribute('href');
    if (!href) return;
    const lower = href.toLowerCase();
    if (!lower.endsWith('.htm') && !lower.endsWith('.html')) return;

    let absolute;
    try {
      absolute = new URL(href, liveScoreBaseUrl).href;
    } catch {
      return;
    }
    if (seen.has(absolute)) return;

    if (heading && segmentSystem !== 'both') {
      const rel = heading.compareDocumentPosition(a);
      const linkIsAfterSixZeroHeading = (rel & Node.DOCUMENT_POSITION_FOLLOWING) !== 0;
      if (segmentSystem === 'ijs' && linkIsAfterSixZeroHeading) return;
      if (segmentSystem === '60' && !linkIsAfterSixZeroHeading) return;
    }

    seen.add(absolute);
    // Index tables are usually Event | Segment — combine so label is e.g. "111 Basic 6 Girls Free Skating"
    const row = a.closest('tr');
    const cells = row ? Array.from(row.querySelectorAll('td, th')) : [];
    let eventCell = '';
    if (cells.length >= 2 && !cells[0].querySelector('a')) {
      eventCell = (cells[0].textContent || '').replace(/\s+/g, ' ').trim();
    }
    const segText = (a.textContent || '').replace(/\s+/g, ' ').trim();
    const label = [eventCell, segText].filter(Boolean).join(' ') || segText;
    if (/start\s*order/i.test(label)) return;
    out.push({ url: absolute, label });
  });

  return out;
}
